import type { Language, SimulationPublic } from './schemas/simulation'

/**
 * 효능·질병 표현 후처리 필터 (식품표시광고법 제8조, 로드맵 P8, 2026-10-26).
 * 프롬프트가 금지해도 모델이 "당뇨에 좋은", "면역력 강화", "cures colds" 를 쓸 때가 있다. 생성·AI 수정 결과의
 * 판매 문구(제품명·tagline·먹는 법·소비자 공정)에서 그 표현을 결정적으로 빼거나 중립 문장으로 바꾼다.
 *
 * 목록은 법이 명백히 막는 것만 담는다: 질병의 예방·치료·완화, 의약품 오인(특효·약효·항암), 건강기능식품 오인 중
 * 수치를 바꾼다는 말(혈당·혈압·콜레스테롤을 낮춤, 면역력 강화, 체지방 감소, 다이어트 효과, 숙취해소).
 * "소화에 도움", "장 건강", "피로회복", "항산화", "디톡스" 같은 경계 표현은 프롬프트로만 막고 여기서는 거르지 않는다
 * (법 해석과 대표 결정 대기 — 오탐이 판매 문구를 망가뜨리는 것을 먼저 피한다).
 *
 * 오탐 기준: "예방·방지·억제·완화"는 질병 이름이 붙을 때만 걸린다(교차오염 예방, 갈변 방지, 산미 완화는 통과).
 * cure·treat·cold 는 음식 말(cured ham, sweet treat, serve cold)이라 질병 목적어가 있을 때만 걸린다.
 * 규정 메모(baseNote·caution·risk)는 "질병 예방 표현 금지" 같은 설명을 써야 하므로 거르지 않는다.
 */

/**
 * 한국어 질병·증상. 암은 낱말 첫 글자일 때만 (고구마암 같은 오탐 방지). lookbehind 대신 앞 글자를 같이 잡는다 —
 * 이 모듈은 @repo/shared 로 앱 번들에도 들어가 import 때 정규식을 만들므로 엔진 지원이 넓은 문법만 쓴다
 */
const KO_DISEASES =
  '당뇨병?|고혈압|저혈압|고지혈증?|동맥경화|심장병|심혈관\\s?질환|뇌졸중|중풍|치매|알츠하이머|파킨슨|관절염|골다공증|변비|아토피|비염|천식|감기|독감|코로나|위염|위궤양|간염|간\\s?질환|지방간|불면증|우울증|염증|비만|탈모|빈혈|숙취|성인병|생활습관병|질병|질환|(?:^|[^가-힣])암'
const KO_CLAIMS =
  '예방|치료|치유|완치|개선|완화|억제|해소|해결|제거|효과|효능|특효|극복|퇴치|케어|잡는|잡아|에\\s?좋|에\\s?효|낫|고치|다스리|방지'

const EN_DISEASES =
  "diabetes|cancers?|tumou?rs?|hypertension|high blood pressure|heart disease|cardiovascular disease|strokes?|dementia|alzheimer'?s|arthritis|osteoporosis|constipation|eczema|asthma|colds|common cold|flu|influenza|covid(?:-19)?|inflammation|obesity|insomnia|depression|hangovers?|diseases?|illness(?:es)?|ulcers?|acne|anemia"
const EN_CLAIM_VERBS =
  'cures?|cured|curing|cure for|treats?|treating|treatment (?:for|of)|prevents?|preventing|prevention of|heals?|healing|fights?|fighting|combats?|reverses?|relieves?|relief (?:from|of)|remed(?:y|ies) for|protects? against|(?:reduces?|lowers?) (?:the )?risk of'

const JA_DISEASES =
  '糖尿病|高血圧|がん|癌|認知症|関節炎|骨粗しょう症|骨粗鬆症|便秘|風邪|かぜ|インフルエンザ|花粉症|アトピー|二日酔い|病気|疾患|生活習慣病|肥満|不眠|炎症|動脈硬化|脳卒中|心臓病|痛風|胃炎'
const JA_CLAIMS =
  '予防|治療|治す|治る|改善|緩和|効く|効果|効能|対策|防ぐ|防止|撃退|解消|に良い|によい|にいい'

const ZH_DISEASES =
  '糖尿病|高血压|高血壓|癌症|肿瘤|腫瘤|心脏病|心臟病|痴呆|关节炎|關節炎|骨质疏松|骨質疏鬆|便秘|感冒|流感|失眠|肥胖|炎症|发炎|發炎|疾病|宿醉|高血脂|痛风|痛風|胃炎|三高'
const ZH_CLAIMS_BEFORE = '预防|預防|治疗|治療|防治|根治|远离|遠離|对抗|對抗|治'
const ZH_CLAIMS_AFTER = '克星|治疗|治療|缓解|緩解|改善|预防|預防'

/** 하나라도 맞으면 효능·질병 표현. 'giu' — 같은 문장의 여러 표현을 다 찾는다 */
const HEALTH_CLAIM_PATTERNS: readonly RegExp[] = [
  // 한국어: 질병 + (조사) + 효능 말. 뒤 [가-힣]* 는 "예방하는", "좋은" 처럼 낱말 끝까지 (제품명에서 통째로 뺀다)
  new RegExp(`(?:${KO_DISEASES})\\s?(?:에|을|를|의|이|가)?\\s?(?:${KO_CLAIMS})[가-힣]*`, 'giu'),
  /(?:치료|완치|특효|만병통치|약효|의약품|항암|처방)[가-힣]*/giu,
  /면역력?\s?(?:을|를|이)?\s?(?:강화|증진|향상|증강|높|올|키우|키워|up|업)[가-힣]*/giu,
  /(?:혈당|혈압|콜레스테롤|중성지방|혈중\s?지방)\s?(?:을|를|이)?\s?(?:낮|떨어|조절|강하|개선|억제|잡)[가-힣]*/giu,
  // 맨 '지방'은 연소·분해만 ("고기의 지방을 제거" 같은 손질 문장은 통과)
  /(?:체지방\s?(?:을|를)?\s?(?:감소|분해|연소|제거|커팅|태우)|지방\s?(?:을|를)?\s?(?:연소|분해|커팅))[가-힣]*/giu,
  /(?:다이어트|체중\s?감량)\s?(?:에\s?)?(?:효과|효능)[가-힣]*/giu,
  /살\s?(?:이\s?)?빠지[가-힣]*/giu,
  // 영어: 효능 동사 + 질병 목적어, 질병 + -fighting/cure/relief, 그리고 수치를 바꾼다는 말
  new RegExp(
    `\\b(?:${EN_CLAIM_VERBS})\\s+(?:(?:the|your|of|against|from|a)\\s+)*(?:${EN_DISEASES})\\b`,
    'giu',
  ),
  new RegExp(
    `\\b(?:${EN_DISEASES})[\\s-]+(?:fighting|preventing|prevention|cure|cures|relief|remedy|treatment|busting|buster)\\b`,
    'giu',
  ),
  // medicinal 은 뺐다 — 삼계탕 같은 한방 요리 소개의 "medicinal herbs"(약재)가 걸린다
  /\b(?:anti-?cancer|anti-?inflammatory|miracle cure|cure-?all|clinically proven)\b/giu,
  /\b(?:boosts?|boosting|strengthens?|strengthening|supports?) (?:your |the )?immun\w*/giu,
  /\bimmun(?:e|ity)[\s-]+(?:boost\w*|support\w*|defen[cs]e)\b/giu,
  /\b(?:lowers?|lowering|reduces?|reducing|regulates?|regulating|controls?) (?:your )?(?:blood sugar|blood glucose|blood pressure|cholesterol)\b/giu,
  /\b(?:blood[\s-]sugar|cholesterol|blood[\s-]pressure)[\s-]+(?:lowering|control\w*|balanc\w*)\b/giu,
  /\b(?:burns? fat|fat[\s-]burn\w*|helps? (?:you )?lose weight|promotes? weight loss|for weight loss|weight[\s-]loss (?:aid|support|benefits?|effect))\b/giu,
  // 일본어: 질병 + (조사) + 효능 말, 그리고 수치를 바꾼다는 말
  new RegExp(`(?:${JA_DISEASES})(?:の|に|を|が)?(?:${JA_CLAIMS})`, 'giu'),
  /(?:特効|薬効|万病|抗がん|抗癌|医薬品|治療)/giu,
  /免疫力?(?:を|が)?(?:高め|上げ|アップ|強化|向上|増強)/giu,
  /(?:血糖値?|血圧|コレステロール|中性脂肪)(?:を|が)?(?:下げ|抑え|低下|コントロール|改善)/giu,
  // やせる 는 뺐다 — 冷やせる(식힐 수 있다)가 걸린다
  /(?:脂肪燃焼|脂肪を燃や|痩せる|痩せられ|ダイエット効果|体重(?:を)?減)/giu,
  // 중국어(간체·번체). 醒酒 는 뺐다 — 醒酒器(디캔터)가 걸린다: 효능 말 + 질병 / 질병 + 효능 말, 그리고 수치를 바꾼다는 말
  new RegExp(`(?:${ZH_CLAIMS_BEFORE})(?:${ZH_DISEASES})`, 'giu'),
  new RegExp(`(?:${ZH_DISEASES})的?(?:${ZH_CLAIMS_AFTER})`, 'giu'),
  /(?:特效|疗效|療效|药效|藥效|抗癌|防癌|包治百病)/giu,
  /(?:提高|增强|增強|提升)免疫力|免疫力(?:提升|增强|增強)/giu,
  /降(?:血糖|血压|血壓|血脂|胆固醇|膽固醇|三高)/giu,
  /(?:燃脂|减肥效果|減肥效果|减肥功效|減肥功效|帮助减肥|幫助減肥|瘦身效果|解酒)/giu,
]

/** 걸린 표현 하나의 자리 (NFKC 로 맞춘 글 기준) */
export interface ClaimSpan {
  start: number
  end: number
  phrase: string
}
/** 판매 문구에서 걷어 낼 표현을 찾는 규칙. 효능·질병(이 파일)과 근거 없는 표현(unsupported-claims.ts)이 같은 정리 함수를 쓴다 */
export type ClaimMatcher = (plain: string) => ClaimSpan[]

/** 정규식 목록으로 자리를 찾는다 ('g' 플래그 정규식) */
export function claimSpansOf(patterns: readonly RegExp[], plain: string): ClaimSpan[] {
  return patterns.flatMap((re) =>
    [...plain.matchAll(re)].map((m) => ({
      start: m.index,
      end: m.index + m[0].length,
      phrase: m[0],
    })),
  )
}

/** 효능·질병 표현의 자리 (P8 규칙) */
export const healthClaimSpans: ClaimMatcher = (plain) => claimSpansOf(HEALTH_CLAIM_PATTERNS, plain)

/** 글 안의 효능·질병 표현을 찾아 돌려준다 (없으면 빈 배열). 전각·호환 문자는 NFKC 로 맞춘 뒤 찾는다 */
export function findHealthClaims(text: string | null | undefined): string[] {
  if (!text) return []
  return healthClaimSpans(text.normalize('NFKC')).map((span) => span.phrase)
}

/** tagline 의 모든 문장이 걸렸을 때 대신 쓰는 중립 문구 (맛·원료 쪽 말만) */
const NEUTRAL_TAGLINE: Record<Language, string> = {
  ko: '재료 본연의 맛을 살린 제품',
  en: 'Made to let its ingredients speak for themselves',
  ja: '素材の味を生かした一品',
  zh: '突出食材本味的产品',
}

const hits = (match: ClaimMatcher, text: string) => match(text.normalize('NFKC')).length > 0

/**
 * 문장 단위로 나눠 걸린 문장만 뺀다. 남는 문장이 없으면 fallback.
 * 숫자 사이의 점(7.5, pH 4.5)은 문장 끝으로 보지 않는다 — 나누면 '단맛 7.5' 가 '단맛 7.'·'5' 로 쪼개져 걸리지 않는다
 */
function dropClaimSentences(match: ClaimMatcher, text: string, fallback: string): string {
  const sentences = text.normalize('NFKC').match(/(?:[^.!?。！？]|\.(?=\d))+[.!?。！？]*/gu) ?? []
  const kept = sentences.map((s) => s.trim()).filter((s) => s && !hits(match, s))
  return kept.length ? kept.join(' ') : fallback
}

/** 걸린 표현의 앞뒤가 공백·문장 끝이면 true — 그 표현만 빼도 이름이 말이 된다 */
function isWordBounded(text: string, start: number, end: number): boolean {
  return (
    (start === 0 || /\s/u.test(text[start - 1] ?? '')) &&
    (end === text.length || /[\s,]/u.test(text[end] ?? ''))
  )
}

/**
 * 제품명: 걸린 표현이 낱말 단위로 떨어지면 그 표현만 뺀다 ("당뇨에 좋은 현미 쿠키" → "현미 쿠키",
 * "Immune-Boosting Ginger Tea" → "Ginger Tea"). 띄어쓰기 없이 붙은 이름(血糖値を下げるお茶)이나 남는 말이 없으면
 * 세부유형(subtype) → 판매 형태(format) → 중립 문구 순으로 쓴다
 */
function cleanName(
  match: ClaimMatcher,
  name: string,
  fallbacks: string[],
  language: Language,
): string {
  const plain = name.normalize('NFKC')
  const spans = match(plain)
  const bounded = spans.every(({ start, end }) => isWordBounded(plain, start, end))
  const stripped = bounded
    ? spans
        .reduce(
          (text, { start, end }) =>
            text.slice(0, start) + ' '.repeat(end - start) + text.slice(end),
          plain,
        )
        .replace(/\s+/gu, ' ')
        .replace(/^[\s,·・、-]+|[\s,·・、-]+$/gu, '')
    : ''
  return (
    [stripped, ...fallbacks]
      .map((c) => c.trim())
      .find((c) => [...c].length >= 2 && !hits(match, c)) ?? NEUTRAL_TAGLINE[language]
  )
}

/** 소비자 공정 한 줄: 같은 순서의 전문 공정 제목 → 걸린 문장만 뺀 줄 → 표현만 지운 줄 */
function cleanPlainStep(match: ClaimMatcher, line: string, stepTitle: string): string {
  const plain = line.normalize('NFKC')
  // 뒤에서부터 지운다. 겹치는 자리(두 규칙이 같은 말을 잡음)는 앞쪽 시작까지 한 번에 지운다
  let struck = plain
  let cut = plain.length
  for (const { start, end } of match(plain).sort((x, y) => y.start - x.start)) {
    struck = struck.slice(0, start) + ' ' + struck.slice(Math.min(end, cut))
    cut = start
  }
  struck = struck.replace(/\s+/gu, ' ').trim()
  return (
    [stepTitle, dropClaimSentences(match, plain, ''), struck].find(
      (c) => c.trim() && !hits(match, c),
    ) ?? '-'
  )
}

/**
 * 결과의 판매 문구(제품명·tagline·먹는 법·소비자 공정)에서 match 가 찾은 표현을 걷어 낸다.
 * 걸린 것이 없으면 같은 객체를 그대로 돌려준다
 */
export function removeClaims<T extends SimulationPublic>(result: T, match: ClaimMatcher): T {
  const { product, plainSteps } = result
  const hit = (text: string) => hits(match, text)
  if (![product.name, product.tagline, product.howToUse, ...plainSteps].some(hit)) return result
  return {
    ...result,
    product: {
      ...product,
      name: hit(product.name)
        ? cleanName(match, product.name, [product.subtype, product.format], result.language)
        : product.name,
      tagline: hit(product.tagline)
        ? dropClaimSentences(match, product.tagline, NEUTRAL_TAGLINE[result.language])
        : product.tagline,
      // 먹는 법이 비면 앱은 그 칩 자리에 소비기한을 보인다 (옛 결과도 없을 수 있는 칸)
      howToUse: hit(product.howToUse) ? '' : product.howToUse,
    },
    // 소비자 공정 줄은 개수(3~5)를 지켜야 하므로 빼지 않고 같은 순서의 전문 공정 제목으로 바꾼다
    plainSteps: plainSteps.map((line, i) =>
      hit(line) ? cleanPlainStep(match, line, result.steps[i]?.title ?? '') : line,
    ),
  }
}

/** 결과의 판매 문구에서 효능·질병 표현을 걷어 낸다. 걸린 것이 없으면 같은 객체를 그대로 돌려준다 */
export function removeHealthClaims<T extends SimulationPublic>(result: T): T {
  return removeClaims(result, healthClaimSpans)
}
